import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import WorkCard from "@/components/base/WorkCard";
import SectionHead from "@/components/base/SectionHead";
import {
  categoryName,
  coverOf,
  episodesOf,
  formatViews,
  getWork,
  relatedWorks,
  specialsOfWork,
} from "@/lib/content";
import { getCover } from "@/assets/covers";

export default function Detail() {
  const { id } = useParams();
  const work = getWork(id);
  const [activeEpisode, setActiveEpisode] = useState(0);
  const [collected, setCollected] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setActiveEpisode(0);
    setCollected(false);
    if (work) {
      document.title = `${work.title} - 亚洲无码高清成人影视`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute(
          "content",
          `${work.title}（${work.year} · ${work.region} · ${categoryName(work.category)}）作品资料：${work.synopsis.slice(0, 60)}`,
        );
      }
    }
  }, [work]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!work) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-primary-500">
            <i className="ri-file-unknow-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-50">作品不存在或已下架</h1>
          <p className="mt-2 max-w-md text-[13px] text-foreground-500">
            该作品编号没有对应的资料记录，可能链接有误。你可以返回分类页重新浏览。
          </p>
          <div className="mt-5 flex gap-2">
            <Link
              to="/category/all"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[13px] font-semibold text-background-950"
            >
              返回作品分类
            </Link>
            <Link
              to="/"
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2.5 text-[13px] text-foreground-300"
            >
              回到首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const episodes = episodesOf(work);
  const current = episodes[activeEpisode] ?? episodes[0];
  const related = relatedWorks(work, 5);
  const inSpecials = specialsOfWork(work.id);
  const categoryLabel = categoryName(work.category);

  const handleCollect = () => {
    setCollected((prev) => !prev);
    setToast(collected ? "已取消收藏（仅本机演示，不保存）" : "已加入收藏（仅本机演示，不保存到服务器）");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("作品链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <SiteLayout>
      <section className="relative overflow-hidden border-b border-background-300/50 bg-background-100">
        <img
          src={coverOf(work)}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-20 blur-2xl"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
        <div className="relative mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-600">
            <Link to="/" className="cursor-pointer whitespace-nowrap transition hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-700" />
            <Link
              to={`/category/${work.category}`}
              className="cursor-pointer whitespace-nowrap transition hover:text-primary-300"
            >
              {categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-700" />
            <span className="truncate text-foreground-300">{work.title}</span>
          </nav>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${work.category}`}
              className="cursor-pointer rounded-md bg-primary-500 px-2.5 py-1 font-heading text-[11px] font-bold text-background-950 transition hover:bg-primary-400"
            >
              {categoryLabel}
            </Link>
            <span className="rounded-md border border-accent-600/50 px-2 py-1 text-[10px] font-semibold text-accent-300">
              18+
            </span>
            {work.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md border border-background-300/70 px-2 py-1 text-[10px] text-foreground-500"
              >
                #{tag}
              </span>
            ))}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-50 md:text-[34px]">
            {work.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-earth-line text-primary-500" />
              {work.region}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line text-primary-500" />
              {work.year}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-play-list-2-line text-primary-500" />
              {work.episodeCount} 话目录
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line text-primary-500" />
              {work.duration}
            </span>
            <span className="flex items-center gap-1 text-primary-300">
              <i className="ri-star-fill text-[10px]" />
              {work.rating.toFixed(1)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-primary-500" />
              {formatViews(work.views)} 浏览
            </span>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleCollect}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2.5 text-[13px] transition ${
                collected
                  ? "border-primary-500 bg-primary-500 font-semibold text-background-950"
                  : "border-background-300/80 text-foreground-200 hover:border-primary-600/70 hover:text-primary-300"
              }`}
            >
              <i className={collected ? "ri-bookmark-fill" : "ri-bookmark-line"} />
              {collected ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/80 px-4 py-2.5 text-[13px] text-foreground-200 transition hover:border-primary-600/70 hover:text-primary-300"
            >
              <i className="ri-share-forward-line" />
              分享
            </button>
            <Link
              to={`/category/${work.category}`}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2.5 text-[13px] text-foreground-500 transition hover:text-primary-300"
            >
              更多{categoryLabel}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
          <div className="lg:sticky lg:top-[92px] lg:self-start">
            <div className="relative w-full overflow-hidden rounded-lg border border-background-300/60 bg-background-200">
              <div className="relative aspect-[3/4] w-full">
                <img
                  src={coverOf(work)}
                  alt={`${work.title} 封面大图`}
                  title={`${work.title} · ${categoryLabel} · ${work.year}`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-transparent to-transparent" />
                <span className="absolute bottom-3 left-3 rounded bg-background-950/70 px-2 py-1 text-[10px] tracking-wide text-foreground-200 backdrop-blur-sm">
                  {work.studio} · {work.director}
                </span>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-300/60 bg-background-100 p-4">
              <dl className="space-y-2.5 text-[12px]">
                {[
                  { label: "作品编号", value: work.id.toUpperCase() },
                  { label: "制作地区", value: work.region },
                  { label: "上映年份", value: String(work.year) },
                  { label: "剧集数量", value: `${work.episodeCount} 话` },
                  { label: "单集时长", value: work.duration },
                  { label: "导演", value: work.director },
                  { label: "出品", value: work.studio },
                ].map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-3">
                    <dt className="flex-shrink-0 text-foreground-600">{row.label}</dt>
                    <dd className="text-right text-foreground-300">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-3 flex items-start gap-1.5 border-t border-background-300/50 pt-3 text-[10px] leading-relaxed text-foreground-600">
                <i className="ri-information-line mt-0.5 text-primary-600" />
                以上信息为资料展示，本站不提供播放、下载或购买入口。
              </p>
            </div>
          </div>

          <div className="min-w-0">
            <section>
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-50">
                <span className="h-4 w-1 rounded-full bg-primary-500" />
                作品简介
              </h2>
              <p className="mt-3 text-[13px] leading-[1.9] text-foreground-400">{work.synopsis}</p>
              <p className="mt-3 text-[13px] leading-[1.9] text-foreground-500">
                本作由 {work.director} 执导，{work.studio} 出品，共 {work.episodeCount} 话，单集约{" "}
                {work.duration}。整体风格围绕「{work.tags.join("」「")}」展开，适合偏好
                {categoryLabel}题材的观众按目录顺序查阅资料。以下剧集目录为文字索引，点击可查看单集简介。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {work.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/all?keyword=${encodeURIComponent(tag)}`}
                    className="cursor-pointer whitespace-nowrap rounded-full border border-background-300/60 px-3 py-1 text-[11px] text-foreground-400 transition hover:border-primary-700/70 hover:text-primary-300"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </section>

            <section className="mt-9">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-50">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  剧集目录
                </h2>
                <span className="text-[11px] text-foreground-600">共 {episodes.length} 话 · 仅目录信息展示</span>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div className="max-h-[330px] overflow-y-auto rounded-lg border border-background-300/60 bg-background-100 p-1.5">
                  {episodes.map((episode, index) => (
                    <button
                      key={episode.no}
                      type="button"
                      onClick={() => setActiveEpisode(index)}
                      className={`flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2.5 text-left transition ${
                        index === activeEpisode ? "bg-background-200" : "hover:bg-background-200/60"
                      }`}
                    >
                      <span
                        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded font-heading text-[11px] font-semibold ${
                          index === activeEpisode
                            ? "bg-primary-500 text-background-950"
                            : "bg-background-300/70 text-foreground-400"
                        }`}
                      >
                        {String(episode.no).padStart(2, "0")}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block truncate text-[12.5px] ${
                            index === activeEpisode ? "text-primary-300" : "text-foreground-200"
                          }`}
                        >
                          {episode.title}
                        </span>
                        <span className="mt-0.5 block text-[10px] text-foreground-600">{episode.duration}</span>
                      </span>
                      <i
                        className={`flex-shrink-0 text-xs ${
                          index === activeEpisode ? "ri-checkbox-circle-fill text-primary-400" : "ri-arrow-right-s-line text-foreground-700"
                        }`}
                      />
                    </button>
                  ))}
                </div>

                <div className="rounded-lg border border-background-300/60 bg-background-100 p-4">
                  <span className="inline-flex items-center gap-1 rounded bg-background-300/60 px-2 py-1 text-[10px] text-primary-300">
                    <i className="ri-file-text-line" />
                    目录资料
                  </span>
                  <h3 className="mt-3 font-heading text-[15px] font-semibold text-foreground-100">
                    {current.title}
                  </h3>
                  <p className="mt-1 text-[11px] text-foreground-600">
                    {current.duration} · {work.region} · 收录于 {work.title}
                  </p>
                  <p className="mt-3 text-[12.5px] leading-[1.9] text-foreground-400">{current.synopsis}</p>
                  <div className="mt-4 flex items-center gap-2 border-t border-background-300/50 pt-3 text-[10px] text-foreground-600">
                    <i className="ri-shield-check-line text-primary-600" />
                    本页仅展示分集文字简介，不包含播放或下载内容。
                  </div>
                </div>
              </div>
            </section>

            {inSpecials.length > 0 ? (
              <section className="mt-9">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-50">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  收录于专题
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {inSpecials.map((special) => (
                    <Link
                      key={special.id}
                      to={`/special/${special.id}`}
                      className="group flex cursor-pointer items-center gap-3 rounded-lg border border-background-300/60 bg-background-100 px-3 py-2.5 transition hover:border-primary-700/60"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded bg-primary-500/15 text-primary-400">
                        <i className="ri-stack-line" />
                      </span>
                      <span>
                        <span className="block text-[12.5px] text-foreground-200 group-hover:text-primary-300">
                          {special.title}
                        </span>
                        <span className="mt-0.5 block text-[10px] text-foreground-600">
                          {special.workIds.length} 部作品 · {special.updatedAt}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-600" />
                    </Link>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </div>

      <section className="border-y border-background-300/50 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-12">
          <SectionHead
            eyebrow="Related"
            title="相关推荐"
            desc={`根据「${categoryLabel}」题材与标签匹配的同类作品资料。`}
            actionLabel={`更多${categoryLabel}`}
            actionTo={`/category/${work.category}`}
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
            {related.map((item) => (
              <WorkCard key={item.id} work={item} />
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto flex w-full max-w-[1440px] flex-wrap items-center justify-between gap-3 px-4 py-8 md:px-8">
        <div className="flex items-center gap-2 text-[12px] text-foreground-600">
          <i className="ri-arrow-left-line" />
          <Link to={`/category/${work.category}`} className="cursor-pointer transition hover:text-primary-300">
            返回{categoryLabel}
          </Link>
          <span className="text-foreground-700">/</span>
          <Link to="/" className="cursor-pointer transition hover:text-primary-300">
            回到首页
          </Link>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-2 text-[12px] text-foreground-400 transition hover:border-primary-600/70 hover:text-primary-300"
        >
          <i className="ri-link" />
          复制作品链接
        </button>
      </div>

      {toast ? (
        <div className="fade-in fixed bottom-6 left-1/2 z-[60] max-w-[90vw] -translate-x-1/2 rounded-md border border-background-300/70 bg-background-100 px-4 py-2.5 text-center text-[12px] text-foreground-100">
          {toast}
        </div>
      ) : null}

      {shareOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center">
          <button
            type="button"
            aria-label="关闭分享"
            onClick={() => setShareOpen(false)}
            className="absolute inset-0 cursor-pointer bg-background-950/75"
          />
          <div className="fade-in relative w-full max-w-md rounded-t-xl border border-background-300/60 bg-background-100 p-5 md:rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-50">分享作品资料</h3>
                <p className="mt-1 text-[11px] text-foreground-600">{work.title} · {work.year}</p>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                aria-label="关闭分享"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-400"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <button
              type="button"
              onClick={handleCopy}
              className="mt-4 flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300/70 px-3 py-3 text-left transition hover:border-primary-600/70"
            >
              <span className="flex items-center gap-2 text-[13px] text-foreground-200">
                <i className="ri-link text-primary-400" />
                复制链接
              </span>
              <span className="text-[11px] text-foreground-600">推荐</span>
            </button>

            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                { icon: "ri-wechat-line", label: "微信" },
                { icon: "ri-weibo-line", label: "微博" },
                { icon: "ri-twitter-x-line", label: "X" },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => setToast(`${item.label}渠道为界面演示，请使用「复制链接」分享`)}
                  className="flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-background-300/60 py-3 text-[11px] text-foreground-400 transition hover:border-primary-600/60 hover:text-primary-300"
                >
                  <i className={`${item.icon} text-lg`} />
                  {item.label}
                </button>
              ))}
            </div>

            <p className="mt-3 flex items-start gap-1.5 text-[10px] leading-relaxed text-foreground-600">
              <i className="ri-information-line mt-0.5 text-primary-600" />
              分享功能仅作界面演示，不会保存任何数据；复制链接为真实操作。
            </p>
          </div>
        </div>
      ) : null}
    </SiteLayout>
  );
}